
/* ==========================================================================
   SFF RELATIONAL SPATIAL CONTRACT — V1
   Shared by /db Relational GNN runtime and the 3D Workbench.
   This is an orientation vocabulary / weak layout prior, NOT fitment authority.
   ========================================================================== */

export const RELATIONAL_SPATIAL_CONTRACT_VERSION = 1;

export const ORIENTATION_MATRICES_24 = Object.freeze([[1,0,0,0,1,0,0,0,1],[0,-1,0,1,0,0,0,0,1],[-1,0,0,0,-1,0,0,0,1],[0,1,0,-1,0,0,0,0,1],[0,0,1,0,1,0,-1,0,0],[0,0,1,1,0,0,0,1,0],[0,0,1,0,-1,0,1,0,0],[0,0,1,-1,0,0,0,-1,0],[-1,0,0,0,1,0,0,0,-1],[0,1,0,1,0,0,0,0,-1],[1,0,0,0,-1,0,0,0,-1],[0,-1,0,-1,0,0,0,0,-1],[0,0,-1,0,1,0,1,0,0],[0,0,-1,1,0,0,0,-1,0],[0,0,-1,0,-1,0,-1,0,0],[0,0,-1,-1,0,0,0,1,0],[1,0,0,0,0,-1,0,1,0],[0,-1,0,0,0,-1,1,0,0],[-1,0,0,0,0,-1,0,-1,0],[0,1,0,0,0,-1,-1,0,0],[-1,0,0,0,0,1,0,1,0],[0,1,0,0,0,1,1,0,0],[1,0,0,0,0,1,0,-1,0],[0,-1,0,0,0,1,-1,0,0]].map(row => Object.freeze(row)));

const IDENTITY_CLASS = 0;
const SANDWICH_MOBO_CLASS = 3;
const SANDWICH_GPU_CLASS = 6;
const REFERENCE_MOBO_CLASS = 1;
const REFERENCE_GPU_CLASS = 17;

function normalizeLayout(layout) {
  const raw = String(layout || 'reference').trim().toLowerCase();
  if (raw.includes('sandwich')) return 'sandwich';
  if (raw.includes('reference') || raw.includes('standard')) return 'reference';
  if (raw === 'console') return 'console';
  if (raw === 'bench') return 'bench';
  if (raw === 'apu' || raw === 'apu_only') return 'apu';
  return 'reference';
}

export function getRelationalOrientationClass(layout, nodeType) {
  const family = normalizeLayout(layout);
  const node = String(nodeType || '').trim().toLowerCase();

  // Mirrors the current 3D Simulator anchor orientations. These are intentionally
  // weak topology priors; they are not proof that a specific case uses this mount.
  if (node === 'psu' || node === 'case') return IDENTITY_CLASS;

  if (family === 'reference') {
    if (node === 'mobo' || node === 'motherboard' || node === 'cooler' || node === 'aircooler') return REFERENCE_MOBO_CLASS;
    if (node === 'gpu') return REFERENCE_GPU_CLASS;
  } else {
    // Current Simulator's sandwich / console / fallback branches use this family.
    if (node === 'mobo' || node === 'motherboard' || node === 'cooler' || node === 'aircooler') return SANDWICH_MOBO_CLASS;
    if (node === 'gpu') return SANDWICH_GPU_CLASS;
  }

  // AIO/radiator placement is deliberately not asserted by this contract yet.
  return IDENTITY_CLASS;
}

export function orientationMatrixForClass(index) {
  const idx = Number(index);
  return Array.isArray(ORIENTATION_MATRICES_24[idx]) ? [...ORIENTATION_MATRICES_24[idx]] : [...ORIENTATION_MATRICES_24[0]];
}

export function nearestRelationalOrientationClass(matrixLike) {
  if (!Array.isArray(matrixLike) || matrixLike.length < 9) return 0;
  const input = matrixLike.slice(0, 9).map(Number);
  if (input.some(v => !Number.isFinite(v))) return 0;

  let bestIdx = 0;
  let bestDistance = Infinity;
  for (let i = 0; i < ORIENTATION_MATRICES_24.length; i++) {
    const candidate = ORIENTATION_MATRICES_24[i];
    let distance = 0;
    for (let j = 0; j < 9; j++) {
      const diff = input[j] - candidate[j];
      distance += diff * diff;
    }
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export function threeEulerToRelationalMatrix(euler) {
  const x = Number(euler?.x || 0), y = Number(euler?.y || 0), z = Number(euler?.z || 0);
  const a = Math.cos(x), b = Math.sin(x);
  const c = Math.cos(y), d = Math.sin(y);
  const e = Math.cos(z), f = Math.sin(z);
  return [
    c * e, -c * f, d,
    a * f + b * e * d, a * e - b * f * d, -b * c,
    b * f - a * e * d, b * e + a * f * d, a * c
  ];
}

export function relationalOrientationClassFromEuler(euler) {
  return nearestRelationalOrientationClass(threeEulerToRelationalMatrix(euler));
}

export function getRelationalLayoutSpatialPrior(layout, caseDims, limits = {}) {
  const dims = Array.isArray(caseDims) && caseDims.length >= 3 ? caseDims.slice(0, 3).map(Number) : [300, 150, 200];
  const [l, w, h] = dims.map((v, i) => Number.isFinite(v) && v > 0 ? v : [300, 150, 200][i]);
  const side = 3;
  const maxGpuL = Number.isFinite(Number(limits.maxGPU)) ? Number(limits.maxGPU) : Math.max(0, l - 10);
  const maxGpuT = Number.isFinite(Number(limits.maxGPUT)) ? Number(limits.maxGPUT) : Math.max(20, w * 0.4);
  const maxGpuH = Number.isFinite(Number(limits.maxGPUH)) && Number(limits.maxGPUH) > 0 ? Number(limits.maxGPUH) : Math.max(100, h - 20);
  const family = normalizeLayout(layout);
  const prior = { case: [0, 0, 0], aio: [0, h / 2 - 20, 0] };

  if (family === 'sandwich') {
    const left = -w / 2 + side;
    const spine = left + maxGpuT;
    prior.mobo = [spine + 22, 0, -l / 2 + 85 + side];
    prior.gpu = [left + maxGpuT / 2, 0, -l / 2 + maxGpuL / 2 + side];
    prior.psu = [spine + 10, -h / 4, l / 2 - 70];
  } else if (family === 'reference') {
    const right = w / 2 - side;
    const floor = -h / 2 + side;
    const ceiling = h / 2 - side;
    prior.mobo = [right - 22, ceiling - 85, -l / 2 + 85 + side];
    prior.gpu = [right - maxGpuH / 2, floor + maxGpuT / 2, -l / 2 + maxGpuL / 2 + side];
    prior.psu = [w / 4, h / 4, l / 2 - 80];
  } else {
    prior.mobo = [0, -h / 4, -l / 2 + 85 + side];
    prior.gpu = [0, h / 4, -l / 2 + maxGpuL / 2 + side];
    prior.psu = [0, -h / 4, l / 2 - 70];
  }
  prior.cooler = [...prior.mobo];
  return prior;
}
